import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../utils/api'
import { useAuth } from '../contexts/AuthContext'

interface Course {
  id: number
  title: string
  instructor: string
  credits: number
  schedule: string
  capacity: number
  enrolled: number
  status: string
  deadline: string
  year: number
  season: string
}

const Courses = () => {
  const [courses, setCourses] = useState<Course[]>([])
  const [semesters, setSemesters] = useState<any[]>([])
  const [selectedSemester, setSelectedSemester] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>('')
  const { user } = useAuth()

  useEffect(() => {
    loadSemesters()
  }, [])

  useEffect(() => {
    loadCourses()
  }, [selectedSemester])

  const loadSemesters = async () => {
    try {
      const response = await api.get('/semesters')
      const semestersData = response.data || []
      setSemesters(semestersData)
      const active = semestersData.find((s: any) => s.is_active === 1)
      if (active) {
        setSelectedSemester(active.id)
      } else if (semestersData.length > 0) {
        setSelectedSemester(semestersData[0].id)
      }
      setLoading(false)
    } catch (error: any) {
      console.error('학기 로드 오류:', error)
      setError('학기 정보를 불러올 수 없습니다. 서버가 실행 중인지 확인해주세요.')
      setLoading(false)
    }
  }

  const loadCourses = async () => {
    if (semesters.length === 0 && selectedSemester === null) {
      return // 학기 정보가 로드되기 전에는 실행하지 않음
    }
    setLoading(true)
    setError('')
    try {
      const params = selectedSemester ? { semesterId: selectedSemester } : {}
      const response = await api.get('/courses', { params })
      setCourses(response.data || [])
    } catch (error: any) {
      console.error('강의 로드 오류:', error)
      setError('강의 목록을 불러올 수 없습니다.')
      setCourses([])
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
  }

  return (
    <div className="container">
      <h1 style={{ marginBottom: '20px' }}>강의 목록</h1>
      
      {error && (
        <div className="alert alert-error">
          {error}
          <br />
          <small>백엔드 서버가 실행 중인지 확인해주세요. (http://localhost:3000)</small>
        </div>
      )}

      {loading && semesters.length === 0 ? (
        <div className="loading">로딩 중...</div>
      ) : (
        <>
          <div className="card" style={{ marginBottom: '20px' }}>
            <div className="form-group">
              <label>학년도/계절 선택</label>
              <select 
                value={selectedSemester || ''} 
                onChange={(e) => setSelectedSemester(e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">전체</option>
                {semesters.map(sem => (
                  <option key={sem.id} value={sem.id}>
                    {sem.year}년 {sem.season}
                  </option>
                ))}
              </select>
            </div>
          </div>

      {!loading && courses.length === 0 ? (
        <div className="card">
          <p style={{ textAlign: 'center', color: '#666' }}>등록된 강의가 없습니다.</p>
        </div>
      ) : loading ? (
        <div className="loading">강의 목록을 불러오는 중...</div>
      ) : (
        <div className="grid">
          {courses.map(course => (
            <div key={course.id} className="card">
              <h3 style={{ marginBottom: '10px' }}>{course.title}</h3>
              <p style={{ color: '#666', marginBottom: '10px' }}>
                {course.year}년 {course.season}
              </p>
              <p><strong>교수:</strong> {course.instructor}</p>
              <p><strong>학점:</strong> {course.credits}학점</p>
              <p><strong>일정:</strong> {course.schedule}</p>
              <p><strong>정원:</strong> {course.enrolled}/{course.capacity}</p>
              {course.deadline && (
                <p><strong>마감일:</strong> {formatDate(course.deadline)}</p>
              )}
              <p style={{ marginTop: '10px' }}>
                <span className={`btn ${course.status === 'open' ? 'btn-success' : 'btn-secondary'}`} style={{ padding: '5px 10px', fontSize: '12px' }}>
                  {course.status === 'open' ? '신청 가능' : '마감'}
                </span>
              </p>
              {user && course.status === 'open' && (
                <Link 
                  to={`/enroll/${course.id}`} 
                  className="btn btn-primary" 
                  style={{ marginTop: '10px', display: 'block', textAlign: 'center' }}
                >
                  수강신청
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
        </>
      )}
    </div>
  )
}

export default Courses
